import React, { useState, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Checkbox, Box, Alert, CircularProgress,
  TextField, IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  CreditCard as CardIcon,
  Receipt as CheckIcon,
} from '@mui/icons-material';
import { axiosPrivate } from '../../../api/axios';

const IssueRefundModal = ({ open, onClose, order, orderItems, customer, token }) => {
  const [refundMethod, setRefundMethod] = useState('card');
  const [selectedItems, setSelectedItems] = useState({});
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  //don't filter them since isRefunded isn't used
  const refundableItems = useMemo(
    () => (orderItems || []),//.filter(item => !item.isRefunded),
    [orderItems]
  );

  const totalRefund = useMemo(() =>
    refundableItems
      .filter(item => selectedItems[String(item.OrderItemID)])
      .reduce((sum, item) => sum + parseFloat(item.TotalPrice || item.UnitPrice || 0), 0),
    [selectedItems, refundableItems]
  );

  const allSelected = refundableItems.length > 0 &&
    refundableItems.every(item => selectedItems[String(item.OrderItemID)]);

  const handleToggle = (itemId) =>
    setSelectedItems(prev => ({ ...prev, [String(itemId)]: !prev[String(itemId)] }));

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedItems({});
    } else {
      const all = {};
      refundableItems.forEach(i => { all[String(i.OrderItemID)] = true; });
      setSelectedItems(all);
    }
  };

  const handleClose = () => {
    setSelectedItems({});
    setNotes('');
    setError(null);
    setRefundMethod('card');
    onClose(false, null);
  };

  const handleSubmit = async () => {
    if (!order?.OrderID) {
      setError('No order selected.');
    return;
  }
    const items = refundableItems
      .filter(i => selectedItems[String(i.OrderItemID)])
      .map(i => ({
        orderItemId: i.OrderItemID,
        amount: parseFloat(i.TotalPrice || i.UnitPrice || 0),
        description: i.Description || `Item #${i.OrderItemID}`,
      }));

    if (items.length === 0) {
      setError('Please select at least one item to refund.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    //console.log(refundMethod, items, notes);
    try {
      const res = await axiosPrivate.post(
        `/api/orders/${order.OrderID}/refund`,
        { refundMethod, items, notes },
      );
      onClose(true, res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to issue refund. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fmt = (v) => {
    const n = Number(v) || 0;
    return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const shortName = (desc) => {
    if (!desc) return 'Item';
    return desc.includes('—') ? desc.split('—').pop().trim() : desc;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pr: 6, pb: 0.5 }}>
        Issue refund
        <Typography variant="body2" color="text.secondary" fontWeight={400}>
          Order #{order?.OrderID} — {customer?.name || ''}
        </Typography>
        <IconButton onClick={handleClose} size="small" sx={{ position: 'absolute', right: 8, top: 10 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Refund Method */}
        <Typography variant="overline" display="block" sx={{ mb: 1, color: '#6b7280', letterSpacing: '0.08em' }}>
          Refund Method
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <Button
            size="small"
            variant={refundMethod === 'card' ? 'contained' : 'outlined'}
            startIcon={<CardIcon fontSize="small" />}
            onClick={() => setRefundMethod('card')}
            sx={refundMethod === 'card'
              ? { bgcolor: '#003063', '&:hover': { bgcolor: '#002145' }, textTransform: 'none', fontWeight: 600 }
              : { textTransform: 'none' }}
          >
            Refund to original card
          </Button>
          <Button
            size="small"
            variant={refundMethod === 'check' ? 'contained' : 'outlined'}
            startIcon={<CheckIcon fontSize="small" />}
            onClick={() => setRefundMethod('check')}
            sx={refundMethod === 'check'
              ? { bgcolor: '#b45309', '&:hover': { bgcolor: '#92400e' }, textTransform: 'none', fontWeight: 600 }
              : { textTransform: 'none' }}
          >
            Refund by check
          </Button>
        </Box>

        {refundMethod === 'card' && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Funds will be returned automatically to the card used at checkout.
          </Typography>
        )}
        {refundMethod === 'check' && (
          <Alert
            severity="warning"
            icon={false}
            sx={{ mb: 2, fontSize: '0.875rem', bgcolor: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e' }}
          >
            A check must be manually issued to this customer. Mark as issued once sent.
          </Alert>
        )}

        {/* Items to Refund */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
          <Typography variant="overline" sx={{ color: '#6b7280', letterSpacing: '0.08em' }}>
            Items to Refund
          </Typography>
          <Button size="small" onClick={handleSelectAll} sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
            {allSelected ? 'Deselect all' : 'Select all'}
          </Button>
        </Box>

        <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden', mb: 2 }}>
          {refundableItems.length === 0 ? (
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">All items have already been refunded.</Typography>
            </Box>
          ) : (
            refundableItems.map((item, idx) => {
              const id = String(item.OrderItemID);
              const checked = Boolean(selectedItems[id]);
              const isLast = idx === refundableItems.length - 1;
              return (
                <Box
                  key={item.OrderItemID}
                  onClick={() => handleToggle(item.OrderItemID)}
                  sx={{
                    display: 'flex', alignItems: 'center',
                    px: 1.5, py: 0.75, cursor: 'pointer',
                    bgcolor: checked ? '#eff6ff' : 'white',
                    borderBottom: isLast ? 'none' : '1px solid #f3f4f6',
                    '&:hover': { bgcolor: checked ? '#dbeafe' : '#f9fafb' },
                    transition: 'background-color 0.1s',
                  }}
                >
                  <Checkbox checked={checked} size="small" sx={{ mr: 1, p: 0.25 }} onChange={() => {}} />
                  <Typography variant="body2" sx={{ flex: 1 }}>{shortName(item.Description)}</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {fmt(item.TotalPrice || item.UnitPrice)}
                  </Typography>
                </Box>
              );
            })
          )}
        </Box>

        {/* Notes (check refund only) */}
        {refundMethod === 'check' && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="overline" display="block" sx={{ mb: 0.75, color: '#6b7280', letterSpacing: '0.08em' }}>
              Notes
            </Typography>
            <TextField
              fullWidth multiline rows={3} size="small"
              placeholder="Add a note (e.g. check #, mailing address, date sent)..."
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </Box>
        )}

        {/* Refund Total */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1.5, borderTop: '1px solid #f3f4f6' }}>
          <Typography variant="body2" color="text.secondary">Refund total</Typography>
          <Typography variant="body1" fontWeight={700}>{fmt(totalRefund)}</Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 2, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" disabled={isSubmitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || totalRefund <= 0}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            bgcolor: '#003063', '&:hover': { bgcolor: '#002145' },
            textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em',
          }}
        >
          {isSubmitting
            ? 'Processing...'
            : refundMethod === 'check' ? 'Mark Refund Issued' : 'Issue Refund'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IssueRefundModal;
